import { Button, DataTable, Form, Modal, ResultsCard, ResultsToolbar } from 'shared/components/ui';
import React, { useState, useEffect } from 'react';
import { Person, Add, Save, Search, Clear, Visibility, VisibilityOff } from '@mui/icons-material';

import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { Toast } from '../../../shared/components/Toast';
import { useUsuarios } from '../hooks/useUsuarios';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

const FORM_INICIAL = { usuario: '', password: '', passwordConfirm: '', idRol: '', procesador: false };

const GrillaUsuarios = () => {
  const { usuarios, roles, loading, cargarRoles, buscar, guardar, actualizar, cambiarEstado } = useUsuarios();
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [filtroRol, setFiltroRol] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(FORM_INICIAL);
  const [toast, setToast] = useState(null);
  const [confirmToggle, setConfirmToggle] = useState(null);
  const [clavesVisibles, setClavesVisibles] = useState({});

  useEffect(() => {
    cargarRoles();
    buscar('', null);
  }, []); // eslint-disable-line

  const handleBuscar = () => buscar(filtroUsuario, filtroRol || null);
  const handleLimpiar = () => { setFiltroUsuario(''); setFiltroRol(''); buscar('', null); };

  const abrirNuevo = () => { setForm(FORM_INICIAL); setModal({ mode: 'nuevo' }); };
  const abrirEditar = (item) => {
    setForm({ usuario: item.usuario, password: '', passwordConfirm: '', idRol: String(item.idRol ?? ''), procesador: item.procesador === '1' });
    setModal({ mode: 'editar', item });
  };

  const passwordsValidas = () => {
    if (modal?.mode === 'nuevo') return form.password.length > 0 && form.password === form.passwordConfirm;
    // En edición: si no ingresa password, OK; si ingresa, deben coincidir
    if (form.password === '') return true;
    return form.password === form.passwordConfirm;
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    if (!passwordsValidas()) {
      setToast({ message: 'Las contraseñas no coinciden.', type: 'error' });
      return;
    }
    try {
      if (modal.mode === 'editar') {
        await actualizar(modal.item.id, { IdRol: parseInt(form.idRol), Procesador: form.procesador, Password: form.password || null });
        setToast({ message: 'Usuario actualizado con éxito.', type: 'success' });
      } else {
        await guardar({ Usuario: form.usuario.trim().toLowerCase(), Password: form.password, IdRol: parseInt(form.idRol), Procesador: form.procesador });
        setToast({ message: 'Usuario guardado con éxito.', type: 'success' });
      }
      setModal(null);
      buscar(filtroUsuario, filtroRol || null);
    } catch (err) {
      const msg = err?.response?.status === 409 ? 'El nombre de usuario ya existe.' : 'Error al guardar.';
      setToast({ message: msg, type: 'error' });
    }
  };

  const confirmarToggle = async () => {
    try {
      const nuevoActivo = confirmToggle.activo === '1' ? 0 : 1;
      await cambiarEstado(confirmToggle.id, nuevoActivo);
      setToast({ message: `Usuario ${nuevoActivo === 0 ? 'deshabilitado' : 'habilitado'} con éxito.`, type: 'success' });
      buscar(filtroUsuario, filtroRol || null);
    } catch {
      setToast({ message: 'Error al cambiar el estado.', type: 'error' });
    } finally {
      setConfirmToggle(null);
    }
  };

  const passwordMismatch = form.password !== '' && form.passwordConfirm !== '' && form.password !== form.passwordConfirm;

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <Person sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Usuarios
          </h2>
          <span className="page-subtitle">Gestión de usuarios del sistema</span>
        </div>

        <Modal show={modal !== null} onHide={() => setModal(null)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{modal?.mode === 'editar' ? 'Editar Usuario' : 'Nuevo Usuario'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleGuardar}>
            <Modal.Body style={{ padding: '1.75rem' }}>
              <div className="row g-3">
                <div className="col-12">
                  <Form.Group>
                    <Form.Label>Usuario *</Form.Label>
                    <Form.Control
                      type="text"
                      value={form.usuario}
                      onChange={(e) => setForm(f => ({ ...f, usuario: e.target.value }))}
                      required
                      readOnly={modal?.mode === 'editar'}
                      placeholder="usuario..."
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>{modal?.mode === 'nuevo' ? 'Password *' : 'Nueva Password'}</Form.Label>
                    <Form.Control
                      type="password"
                      value={form.password}
                      onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                      required={modal?.mode === 'nuevo'}
                      placeholder="Password..."
                      isInvalid={passwordMismatch}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>{modal?.mode === 'nuevo' ? 'Confirmar Password *' : 'Confirmar Password'}</Form.Label>
                    <Form.Control
                      type="password"
                      value={form.passwordConfirm}
                      onChange={(e) => setForm(f => ({ ...f, passwordConfirm: e.target.value }))}
                      required={modal?.mode === 'nuevo'}
                      placeholder="Password..."
                      isInvalid={passwordMismatch}
                    />
                    <Form.Control.Feedback type="invalid">Las contraseñas no coinciden.</Form.Control.Feedback>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Rol *</Form.Label>
                    <Form.Select value={form.idRol} onChange={(e) => setForm(f => ({ ...f, idRol: e.target.value }))} required>
                      <option value="">Seleccione...</option>
                      {roles.map(r => <option key={r.id} value={r.id}>{r.rol}</option>)}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Procesador</Form.Label>
                    <div style={{ display: 'flex', gap: '0.5rem', paddingTop: '0.25rem' }}>
                      {[{ label: 'No', value: false }, { label: 'Sí', value: true }].map(p => (
                        <Button
                          key={String(p.value)}
                          type="button"
                          size="sm"
                          variant={form.procesador === p.value ? 'primary' : 'outline-secondary'}
                          onClick={() => setForm(f => ({ ...f, procesador: p.value }))}
                          style={{ minWidth: '60px' }}
                        >
                          {p.label}
                        </Button>
                      ))}
                    </div>
                  </Form.Group>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setModal(null)} disabled={loading}>Cancelar</Button>
              <Button type="submit" variant="success" disabled={loading || !form.idRol || passwordMismatch || (modal?.mode === 'nuevo' && !form.password)}>
                <Save sx={{ fontSize: 16, mr: 0.5 }} /> {modal?.mode === 'editar' ? 'Actualizar' : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <div className="filters-card">
          <div className="filters-header"><h3 className="filters-title">Filtros de Búsqueda</h3></div>
          <div className="filters-content">
            <div className="filters-row">
              <div className={`filter-group ${filtroUsuario ? 'filter-active' : ''}`}>
                <label className="filter-label">Usuario</label>
                <input className="filter-input" value={filtroUsuario} onChange={(e) => setFiltroUsuario(e.target.value)} placeholder="usuario..." />
              </div>
              <div className={`filter-group ${filtroRol ? 'filter-active' : ''}`}>
                <label className="filter-label">Rol</label>
                <select className="filter-input" value={filtroRol} onChange={(e) => setFiltroRol(e.target.value)}>
                  <option value="">Todos</option>
                  {roles.map(r => <option key={r.id} value={r.id}>{r.rol}</option>)}
                </select>
              </div>
              <div className="filter-actions">
                <Button className="btn-search" onClick={handleBuscar} disabled={loading}>
                  <Search sx={{ fontSize: 18, mr: 0.5 }} /> Buscar
                </Button>
                <Button className="btn-clear" onClick={handleLimpiar}>
                  <Clear sx={{ fontSize: 18, mr: 0.5 }} /> Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>

        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left"><h3 className="results-title">Resultados</h3></div>
            <div className="results-info">
              <span className="results-count">{usuarios.length} registros</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
              <Button variant="success" size="sm" onClick={abrirNuevo} className="btn-new-turno">
                <Add sx={{ fontSize: 18, mr: 0.5 }} /> Nuevo
              </Button>
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Clave</th>
                  <th>Rol</th>
                  <th style={{ textAlign: 'center' }}>Procesador</th>
                  <th style={{ textAlign: 'center' }}>Estado</th>
                  <th className="action-cell">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.length === 0 ? (
                  <tr><td colSpan={6} className="empty-state">{loading ? 'Cargando...' : 'No hay usuarios disponibles'}</td></tr>
                ) : usuarios.map((u) => (
                  <tr key={u.id} style={u.activo === '0' ? { opacity: 0.5 } : {}}>
                    <td>{u.usuario}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {clavesVisibles[u.id] ? (u.clave || '—') : '••••••'}
                        </span>
                        <button
                          type="button"
                          onClick={() => setClavesVisibles(prev => ({ ...prev, [u.id]: !prev[u.id] }))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
                          aria-label={clavesVisibles[u.id] ? 'Ocultar clave' : 'Mostrar clave'}
                        >
                          {clavesVisibles[u.id] 
                            ? <VisibilityOff sx={{ fontSize: 16, color: '#666' }} /> 
                            : <Visibility sx={{ fontSize: 16, color: '#666' }} />}
                        </button>
                      </div>
                    </td>
                    <td>{u.rol}</td>
                    <td style={{ textAlign: 'center' }}>{u.procesador === '1' ? 'Sí' : 'No'}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '12px',
                        fontSize: '0.75rem', fontWeight: 600,
                        background: u.activo === '0' ? '#fee2e2' : '#d1fae5',
                        color: u.activo === '0' ? '#dc2626' : '#059669',
                      }}>
                        {u.activo === '0' ? 'Deshabilitado' : 'Habilitado'}
                      </span>
                    </td>
                    <td className="action-cell">
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <Button variant="contained" size="sm" sx={{ bgcolor: '#3b82f6 !important', color: '#fff !important', '&:hover': { bgcolor: '#2563eb !important' } }} onClick={() => abrirEditar(u)}>Editar</Button>
                        <Button
                          variant={u.activo === '0' ? 'outline-success' : 'outline-danger'}
                          size="sm"
                          onClick={() => setConfirmToggle({ id: u.id, label: u.usuario, activo: u.activo, idRol: u.idRol, procesador: u.procesador })}
                        >
                          {u.activo === '0' ? 'Habilitar' : 'Deshabilitar'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </div>
        </ResultsCard>
      </div>
      <Footer />

      <ConfirmModal
        show={confirmToggle !== null}
        tone="warning"
        title={confirmToggle?.activo === '0' ? 'Habilitar Usuario' : 'Deshabilitar Usuario'}
        message={`¿Deseas ${confirmToggle?.activo === '0' ? 'habilitar' : 'deshabilitar'} al usuario "${confirmToggle?.label}"?`}
        onConfirm={confirmarToggle}
        onCancel={() => setConfirmToggle(null)}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaUsuarios;
